'use client';

import { motion } from 'motion/react';
import { MapPin, Navigation, Building2, Plane, Trophy, Palette, Home, Anchor, Map } from 'lucide-react';
import Image from 'next/image';

const nearbyPlaces = [
  { icon: Palette, name: 'Dubai Design District', distance: '13 mins' },
  { icon: Plane, name: 'Dubai International Airport', distance: '15 mins' },
  { icon: Building2, name: 'Downtown Dubai', distance: '18 mins' },
  { icon: Anchor, name: 'Dubai Creek Harbour', distance: '20 mins' },
  { icon: Building2, name: 'Burj Al Arab', distance: '25 mins' },
  { icon: Plane, name: 'Sharjah International Airport', distance: '30 mins' },
];

export function LocationSection() {
  return (
    <section id="location" className="bg-[#c19964] py-24 md:py-36">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-[1px] bg-white w-[80px] mx-auto mb-8 origin-center"
          />
          <h2 className="text-white tracking-[0.15em] font-light text-xl md:text-4xl">LOCATION</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Description & Nearby Places */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6 text-white/70" />
                <h3 className="text-white text-md md:text-xl tracking-[0.1em] font-light">
                  Connecting you to the city
                </h3>

              </div>
              
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
                Live close to everything. Gate Eleven offers direct access to Dubai&apos;s key destinations - Downtown, DIFC, and Dubai Airport - just minutes away.
              </p>
              {/* <p className="text-white/70 text-base md:text-lg leading-relaxed">
                Strategic positioning ensures seamless connectivity to business districts, entertainment hubs, and cultural landmarks, making it the perfect address for those who demand excellence.
              </p> */}
            </div>

            {/* Nearby Places */}
            <div className="bg-white/20 border border-white/40 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Navigation className="w-5 h-5 text-white/80" />
                <h4 className="text-white text-lg md:text-xl tracking-[0.1em] font-light">
                  Nearby Landmarks
                </h4>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {nearbyPlaces.map((place, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`flex items-start gap-3 pb-3 border-b border-white/10 last:border-0 ${index >= 4 ? 'hidden lg:flex' : ''
                      }`}
                  >
                    <place.icon className="w-5 h-5 text-white/80 flex-shrink-0" />
                    <div className="flex-1 flex justify-between items-center gap-2">
                      <p className="text-white text-sm tracking-wide">{place.name}</p>
                      <p className="text-white/80 text-xs tracking-wider uppercase whitespace-nowrap">{place.distance}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right Side - Map */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="h-full relative group"
          >

            {/* Map Container with Effects */}
            <div className="relative bg-white/5 border-2 border-white/30 overflow-hidden h-[300px] lg:h-full min-h-[300px] transition-all duration-500 group-hover:border-white/50">
              <Image
                src="/images/map-gate.png"
                alt="Gate Eleven Residences Location Map - Dubai"
                fill
                className="object-cover transition-all duration-500"
                loading="lazy"
                quality={80}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}